import React, { useState, useEffect } from 'react';
import { submitTableData } from '../services/balanceSheetService';

const COLUMNS = [
  { label: 'Xero Code',    key: 'xeroCode',     numeric: false },
  { label: 'Accounts',     key: 'accounts',     numeric: false },
  { label: 'Accounts Type', key: 'accountsType', numeric: false },
  { label: 'Debit',        key: 'debit',        numeric: true  },
  { label: 'Credit',       key: 'credit',       numeric: true  },
];

const SCHEDULE_OPTIONS = [
  'Fixed Assets', 'Stock', 'Debtors',
  'Bank', 'Creditors', 'Provision for Liabilities', 'Creditors >1', 'Share Capital', 'Retained Earning'
];

const ETB_OPTIONS = [
  'Sales',
  'Bank Interest Received',
  'Sundry Receipt',
  'Dividend Income From Subsidiary',
  'Grant Income',
  'Opening Stock',
  'Opening Work In Progress',
  'Materials Purchased',
  'Sub-Contractors',
  'Direct Cost',
  'Closing Stock',
  'Closing Work In Progress',
  'Rent',
  'Rates And Water',
  'Use Of Home',
  'Insurance',
  "Directors' Salaries",
  "Directors' - Employer'S Ni",
  "Directors' Pension Contribution",
  'Wages',
  'Staff - Employer\'S Ni',
  'Employers Pensions',
  'Telephone & Internet Charges',
  'Profit On Sale Of Asset',
  'Printing, Postage And Stationery',
  'Motor Expenses',
  'Advertising',
  'Travel And Subsistence',
  'Repairs And Renewals',
  'Consultancy Fees',
  'Computer Software',
  'Training Costs',
  'Sundry',
  'Work Wear',
  'Accountancy Fees',
  'Legal Fees',
  'Formation Cost',
  'Bank Charges',
  'Light And Heat',
  'Loan Interest',
  'Donation',
  'Fines',
  'Entertainment',
  'Subscriptions',
  'Other Expenses - 2',
  'Other Expenses - 3',
  'Other Expenses - 4',
  'Other Expenses - 5',
  'Plant & Machinery - Depreciation',
  'Fixtures & Fittings - Depreciation',
  'Motor Vehicles - Depreciation',
  'Comp. Equipts. - Depreciation',
  'Interest On Corporation Tax',
  'Corporation Tax',
  'Corporation Tax - Prior Year Adjustments',
  'Deferred Tax',
  'Dividend',
];

function TableComponent({ data, companyName, year, totalDebit, totalCredit, previousYearLabel }) {
  const [rowMeta, setRowMeta] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelStatus, setExcelStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    if (data && data.length > 0) {
      setRowMeta(data.map(() => ({ schedule: '', controlAccount: '', etb: '' })));
    } else {
      setRowMeta([]);
    }
  }, [data]);

  const handleMetaChange = (index, field, value) => {
    setRowMeta((prev) => {
      const updated = [...prev];
      const updatedRow = { ...updated[index], [field]: value };

      // When a Schedule value is selected, default Control Account to that row's Accounts value
      if (field === 'schedule' && value) {
        let defaultCa = data[index]?.accounts ?? '';
        if (defaultCa.toLowerCase().includes('fixtures') && defaultCa.toLowerCase().includes('fittings')) {
          defaultCa = 'Furniture & Fittings';
        }
        updatedRow.controlAccount = defaultCa;
      }

      updated[index] = updatedRow;
      return updated;
    });
  };

  const handleCreateExcel = async () => {
    setExcelStatus({ type: '', message: '' });

    const payload = {
      companyName,
      year,
      previousYearLabel,
      data: data.map((row, index) => ({
        xeroCode:       row.xeroCode       ?? '',
        accounts:       row.accounts       ?? '',
        accountsType:   row.accountsType   ?? '',
        debit:          row.debit          ?? 0,
        credit:         row.credit         ?? 0,
        previousYear:   row.previousYear   ?? null,
        schedule:       rowMeta[index]?.schedule       || '',
        controlAccount: rowMeta[index]?.controlAccount || '',
        etb:            rowMeta[index]?.etb            || '',
      })),
    };

    setExcelLoading(true);
    try {
      const response = await submitTableData(payload);

      // Extract filename from Content-Disposition header if present
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
      const filename = match ? match[2].trim() : 'BalanceSheet_TB.xlsx';

      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      setExcelStatus({ type: 'success', message: `"${filename}" downloaded successfully.` });
    } catch (err) {
      setExcelStatus({ type: 'error', message: 'Failed to generate Excel file. Please try again.' });
    } finally {
      setExcelLoading(false);
    }
  };

  const accountOptions = data
    ? [...new Set(data.map((r) => r.accounts).filter(Boolean))]
    : [];

  // Total columns count for colspan calculations
  // COLUMNS + previousYear + Schedule + Control Account + ETB = COLUMNS.length + 4
  const totalCols = COLUMNS.length + 4;

  if (!data || data.length === 0) {
    return (
      <div className="table-section">
        {(companyName || year) && (
          <div className="company-header mb-3">
            {companyName && <h4 className="company-name">{companyName}</h4>}
            {year && <p className="company-year">{year}</p>}
          </div>
        )}
        <h5>Balance Sheet Data</h5>
        <div className="no-data-message">No data available</div>
      </div>
    );
  }

  return (
    <div className="table-section">
      {/* Company header */}
      {(companyName || year) && (
        <div className="company-header mb-3">
          {companyName && <h4 className="company-name">{companyName}</h4>}
          {year && <p className="company-year">{year}</p>}
        </div>
      )}

      <h5>Balance Sheet Data</h5>

      <datalist id="control-account-list">
        {accountOptions.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>

      <div className="table-responsive">
        <table className="table table-bordered table-hover balance-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={col.numeric ? 'text-right' : ''}
                  scope="col"
                >
                  {col.label}
                </th>
              ))}
              <th scope="col" className="text-right">
                {previousYearLabel || 'Previous Year'}
              </th>
              <th scope="col">Schedule</th>
              <th scope="col">Control Account</th>
              <th scope="col">ETB</th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {COLUMNS.map((col) => (
                  <td
                    key={col.key}
                    className={col.numeric ? 'text-right' : ''}
                  >
                    {row[col.key] !== undefined && row[col.key] !== null
                      ? row[col.key]
                      : '—'}
                  </td>
                ))}

                {/* Previous Year value */}
                <td className="text-right">
                  {row.previousYear !== undefined && row.previousYear !== null
                    ? row.previousYear
                    : '—'}
                </td>

                {/* Schedule dropdown */}
                <td>
                  <select
                    className="form-select form-select-sm table-select"
                    value={rowMeta[index]?.schedule || ''}
                    onChange={(e) =>
                      handleMetaChange(index, 'schedule', e.target.value)
                    }
                    aria-label={`Schedule for row ${index + 1}`}
                  >
                    <option value="">-- Select --</option>
                    {SCHEDULE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </td>

                {/* Control Account input with datalist */}
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm table-input"
                    list="control-account-list"
                    value={rowMeta[index]?.controlAccount || ''}
                    onChange={(e) =>
                      handleMetaChange(index, 'controlAccount', e.target.value)
                    }
                    placeholder="Select or type..."
                    aria-label={`Control Account for row ${index + 1}`}
                  />
                </td>

                {/* ETB dropdown */}
                <td>
                  <select
                    className="form-select form-select-sm table-select"
                    value={rowMeta[index]?.etb || ''}
                    onChange={(e) =>
                      handleMetaChange(index, 'etb', e.target.value)
                    }
                    aria-label={`ETB for row ${index + 1}`}
                  >
                    <option value="">-- Select --</option>
                    {ETB_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>

          {/* Totals + Create Excel footer */}
          <tfoot>
            {(totalDebit !== null || totalCredit !== null) && (
              <tr className="table-totals-row">
                <td></td>
                <td className="totals-label">Total</td>
                <td></td>
                <td className="text-right totals-value">
                  {totalDebit !== null ? totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </td>
                <td className="text-right totals-value">
                  {totalCredit !== null ? totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </td>
                <td colSpan={totalCols - 5}></td>
              </tr>
            )}
            <tr className="create-excel-row">
              <td colSpan={totalCols - 1}></td>
              <td>
                <button
                  className="btn btn-create-excel btn-sm w-100"
                  onClick={handleCreateExcel}
                  disabled={excelLoading}
                >
                  {excelLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Creating...
                    </>
                  ) : (
                    'Create Excel'
                  )}
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {excelStatus.message && (
        <div className={`status-message mt-2 ${excelStatus.type}`} role="alert">
          {excelStatus.message}
        </div>
      )}
    </div>
  );
}

export default TableComponent;
